import { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertTriangle, CheckCircle2, ShieldQuestion, Zap } from "lucide-react";
import { previewAllPendingRulesOnly } from "./api";

const VAULT_LABEL = {
  TAX_CUSTODY: "Tax", SUPPLIER_OBLIGATION: "Supplier",
  CUSTOMER_TRUST: "Customer", INVENTORY_CUSTODY: "Inventory",
  OPERATING_CUSTODY: "Operating", FREE_EQUITY: "Free Equity",
};

/* PreviewSweepModal — dry-run for the no-AI allocator.
   Shows the user EXACTLY what would happen before any DB writes:
     • how many records would be auto-applied (rules hit)
     • how many would be direction-fallback guesses (review-flagged)
     • per-record proposed vault, confidence, classification source
   Two CTAs: Cancel · Proceed with Allocation. */
export default function PreviewSweepModal({ open, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    previewAllPendingRulesOnly()
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [open]);

  const grouped = useMemo(() => {
    if (!data?.preview) return { rules: [], fallback: [] };
    const rules = data.preview.filter((p) => p.classification_source === "rules");
    const fallback = data.preview.filter((p) => p.classification_source === "direction_fallback");
    return { rules, fallback };
  }, [data]);

  const rulesPct = data ? Math.round(data.rules_hit_rate * 100) : 0;
  const fallbackPct = data ? Math.round(data.fallback_rate * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-slate-950 border-slate-800 max-h-[85vh] overflow-y-auto" data-testid="preview-sweep-modal">
        <DialogHeader>
          <DialogTitle className="text-slate-200 flex items-center gap-2">
            <ShieldQuestion className="w-5 h-5 text-cyan-400" />
            Dry-Run Preview — Auto-Allocate (No AI)
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            This shows what the allocator <em>would</em> do. No changes have been made to the database.
          </p>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Computing proposed allocations...
          </div>
        )}

        {error && (
          <div className="px-4 py-3 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-300" data-testid="preview-error">
            {String(error)}
          </div>
        )}

        {data && !loading && (
          <>
            {/* Summary stats — the misclassification rate front-and-centre */}
            <div className="grid grid-cols-2 gap-3" data-testid="preview-stats">
              <div className="p-4 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04]">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-[11px] uppercase tracking-wider text-emerald-300">High Confidence (rules hit)</span>
                </div>
                <div className="text-3xl font-mono text-emerald-300" data-testid="preview-rules-count">{data.would_allocate}</div>
                <div className="text-[11px] text-slate-500">{rulesPct}% of {data.total_records} pending — auto-applyable</div>
              </div>
              <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.04]">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-[11px] uppercase tracking-wider text-amber-300">Direction Guess (needs review)</span>
                </div>
                <div className="text-3xl font-mono text-amber-300" data-testid="preview-fallback-count">{data.would_review}</div>
                <div className="text-[11px] text-slate-500">{fallbackPct}% of {data.total_records} pending — flagged for manual review</div>
              </div>
            </div>

            {fallbackPct > 30 && (
              <div className="px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-300 flex items-start gap-2" data-testid="preview-warning">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  <b>{fallbackPct}% of records will be guesses.</b> These are flagged <code className="text-amber-200">needs_review</code>.
                  Inspect the Review Queue after allocation — do not commit until you've reviewed them.
                </span>
              </div>
            )}

            {/* Direction-fallback list — these are the dangerous ones, show them first */}
            {grouped.fallback.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3" /> Direction-Fallback Guesses ({grouped.fallback.length})
                </div>
                <div className="rounded-lg border border-amber-500/20 overflow-hidden max-h-[200px] overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-amber-500/[0.05] text-slate-500 uppercase text-[9px]">
                      <tr><th className="text-left px-3 py-1.5">Description</th><th className="text-left px-3 py-1.5">Direction</th><th className="text-left px-3 py-1.5">Proposed Vault</th><th className="text-right px-3 py-1.5">Amount</th></tr>
                    </thead>
                    <tbody>
                      {grouped.fallback.slice(0, 25).map((p) => (
                        <tr key={p.record_id} className="border-t border-slate-800/30">
                          <td className="px-3 py-1.5 text-slate-300 truncate max-w-xs">{p.description || "(empty)"}</td>
                          <td className="px-3 py-1.5 text-slate-400 capitalize">{p.direction}</td>
                          <td className="px-3 py-1.5">
                            <Badge className="bg-amber-500/10 text-amber-300 border-none text-[9px]">
                              {VAULT_LABEL[p.proposed_vault] || p.proposed_vault}
                            </Badge>
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono text-slate-400">£{(p.amount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {grouped.fallback.length > 25 && (
                    <div className="px-3 py-1.5 text-[10px] text-slate-500 bg-slate-900/40 text-center">
                      … and {grouped.fallback.length - 25} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Rules-hit list — collapsible, less critical */}
            {grouped.rules.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-300 mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3" /> Rules-Hit (high confidence, {grouped.rules.length})
                </div>
                <div className="rounded-lg border border-slate-800 overflow-hidden max-h-[180px] overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-900/40 text-slate-500 uppercase text-[9px]">
                      <tr><th className="text-left px-3 py-1.5">Description</th><th className="text-left px-3 py-1.5">Vault</th><th className="text-right px-3 py-1.5">Confidence</th></tr>
                    </thead>
                    <tbody>
                      {grouped.rules.slice(0, 15).map((p) => (
                        <tr key={p.record_id} className="border-t border-slate-800/30">
                          <td className="px-3 py-1.5 text-slate-300 truncate max-w-xs">{p.description || "(empty)"}</td>
                          <td className="px-3 py-1.5 text-emerald-300">{VAULT_LABEL[p.proposed_vault] || p.proposed_vault}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-emerald-400">{Math.round(p.confidence * 100)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {grouped.rules.length > 15 && (
                    <div className="px-3 py-1.5 text-[10px] text-slate-500 bg-slate-900/40 text-center">
                      … and {grouped.rules.length - 15} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-700 text-slate-300" data-testid="preview-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading || !data || data.total_records === 0}
            className="bg-emerald-600 hover:bg-emerald-500 gap-2"
            data-testid="preview-confirm-btn"
          >
            <Zap className="w-4 h-4" /> Proceed with Allocation ({data?.total_records || 0})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
